// Rug Room Shots: "Download images" dialog (Review header and the Plan view header), build spec 4.1 and 9.7.
//
// (a) Save into a folder: pages through GET jobs/{id}/downloads/files (50 presigned links a page) and, in
//     Chrome / Edge (window.showDirectoryPicker), writes every image straight into one folder the person picks,
//     three at a time, with a plain fetch(url) (no Authorization header: the links are presigned; never apiCall).
//     A file already in the folder with the same size is skipped, so running again resumes. A DIFFERENT file with
//     the same name (a retouched copy, or an older approved attempt) is kept and listed, unless "Replace files with
//     the same name" is ticked (review fix 2026-10-21: it used to be replaced silently). Each file is written through
//     createWritable() (a temporary copy that only replaces the file when closed); the copy is committed only when
//     every byte arrived, so a short or stopped download never touches a file that was already there, and a file that
//     existed before the run is never removed. A 403 (expired link) reloads that page of links once. Other browsers
//     get "Download one by one" (hidden links, one per second, with Stop).
// (b) Zip files: POST jobs/{id}/exports asks the worker to build zip parts of at most ~2 GB in the private bucket.
//     The list polls every 5 s while a request is queued or building. A part's BUTTON calls GET exports/{id}, which
//     presigns every part fresh (15 minutes), and goes to that part's link at once: the link is never shown,
//     kept in state, copied or reused (the next click fetches a new one).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Box, Alert, Paper, Chip, Tooltip,
  RadioGroup, Radio, FormControlLabel, Checkbox, CircularProgress, LinearProgress, Divider, Collapse,
} from '@mui/material';
import { MdFolderOpen, MdStop, MdDownload, MdArchive, MdRefresh, MdCancel, MdExpandMore, MdExpandLess } from 'react-icons/md';
import rrsApi from './api';
import {
  AI_COLOR, primaryButtonSx, fmtInt, fmtSize, fmtElapsed, fmtNy, errText, serverText, conflictCode, usePolling,
  useNow, NyTime,
} from './common';

const PAGE_SIZE = 50;
const PARALLEL = 3;
const FILE_TIMEOUT_MS = 120000;
const ONE_BY_ONE_MS = 1000;
const CORS_TEXT = 'Saving into a folder needs a one-time storage setting that is not switched on yet. Use Download one by one or the zip files.';
const FALLBACK_TEXT = 'Your browser cannot save into a folder. Files go to your Downloads folder one by one; the browser may ask to allow multiple downloads. Files already there are not skipped.';
const RERUN_TEXT = 'Run again to pick up anything that changed while downloading.';
const NOT_READY_TEXT = 'The worker has not been updated for this yet.';

const SELECTIONS = [
  { value: 'approved', label: 'Approved (latest attempt)' },
  { value: 'succeeded', label: 'All finished images (latest attempt; images not approved yet get -g<genId> in the name)' },
];
const SELECTION_WORD = { approved: 'Approved', succeeded: 'All finished' };

const EXPORT_STATUS = {
  queued: { label: 'Waiting for the worker', color: 'default' },
  building: { label: 'Building', color: 'info' },
  ready: { label: 'Ready', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};
const CHECK_WORDS = {
  live: 'checked live against the image server',
  snapshot: "checked against last night's image list",
  mixed: "checked live, partly against last night's image list",
  unknown: 'could not be checked against the image server',
};
const EXPORT_ERRORS = {
  source_missing: 'An image could not be read from storage.',
  source_changed: 'An image changed while the zip was being built; request the zip files again.',
  upload_failed: 'A zip part could not be uploaded.',
  verify_failed: 'A zip part did not pass its check after upload.',
  internal: 'The worker hit an error.',
};

const canPickFolder = () => typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

class CorsProblem extends Error {}

const newStats = (mode) => ({
  mode, // 'folder' | 'one'
  status: 'running', // running | done | stopped | error
  message: '',
  total: null,
  totalBytes: null,
  leftOut: 0,
  saved: 0,
  skipped: 0,
  kept: 0, // a different file with the same name was already in the folder (Replace off)
  replaced: 0,
  failed: 0,
  started: 0,
  bytesDone: 0,
  current: '',
  startedAt: Date.now(),
  endedAt: null,
  failures: [],
  keptNames: [],
});

// ---------------------------------------------------------------------------
const ExportRow = ({ exp, onPart, onCancel, partBusy, cancelBusy, linkMinutes }) => {
  const st = EXPORT_STATUS[exp.status] || { label: exp.status, color: 'default' };
  const active = exp.status === 'queued' || exp.status === 'building';
  const pctBytes = exp.totalBytes ? Math.min(100, (Number(exp.progressBytes || 0) / Number(exp.totalBytes)) * 100) : 0;
  const parts = Array.isArray(exp.parts) ? exp.parts : [];
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>#{exp.exportId}</Typography>
        <Chip size="small" label={st.label} color={st.color} variant={st.color === 'default' ? 'outlined' : 'filled'} />
        <Typography variant="body2">
          {SELECTION_WORD[exp.selection] || exp.selection} · {fmtInt(exp.fileCount)} image{exp.fileCount === 1 ? '' : 's'} · {fmtSize(exp.totalBytes)}
          {exp.skipNamesOnImageServer ? ' · names already on the image server left out' : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {exp.requestedByEmail ? `${exp.requestedByEmail} · ` : ''}<NyTime ts={exp.createdAt} variant="caption" />
        </Typography>
        {active && (
          <Button size="small" color="error" startIcon={<MdCancel />} onClick={() => onCancel(exp)} disabled={cancelBusy || !!exp.cancelRequestedAt}>
            {exp.cancelRequestedAt ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </Stack>
      {exp.status === 'queued' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          About {fmtInt(exp.estimatedParts || 1)} part{exp.estimatedParts === 1 ? '' : 's'} of up to {fmtSize(exp.maxPartBytes)}. The worker starts it shortly.
        </Typography>
      )}
      {exp.status === 'building' && (
        <Box sx={{ mt: 0.75 }}>
          <LinearProgress variant="determinate" value={pctBytes} sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />
          <Typography variant="caption" color="text.secondary">
            {fmtInt(exp.progressFiles || 0)} of {fmtInt(exp.fileCount)} images · {fmtSize(exp.progressBytes || 0)} of {fmtSize(exp.totalBytes)}
            {exp.estimatedParts ? ` · about ${fmtInt(exp.estimatedParts)} part${exp.estimatedParts === 1 ? '' : 's'}` : ''}
          </Typography>
        </Box>
      )}
      {exp.status === 'failed' && (
        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
          {EXPORT_ERRORS[exp.errorCode] || 'The zip files could not be built.'}{exp.errorMessage ? ` (${exp.errorMessage})` : ''} Request them again to retry.
        </Typography>
      )}
      {exp.status === 'ready' && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {fmtInt(exp.filesInZip)} image{exp.filesInZip === 1 ? '' : 's'} in {fmtInt(exp.partCount || parts.length)} zip file{(exp.partCount || parts.length) === 1 ? '' : 's'}
            {exp.filesSkipped ? `, ${fmtInt(exp.filesSkipped)} left out (see the manifest CSV in each zip)` : ''}
            {exp.imageServerCheck && CHECK_WORDS[exp.imageServerCheck] ? `; file names ${CHECK_WORDS[exp.imageServerCheck]}` : ''}
            {exp.nameClashCount ? `; ${fmtInt(exp.nameClashCount)} name${exp.nameClashCount === 1 ? ' was' : 's were'} already on the image server${exp.skipNamesOnImageServer ? ' (left out)' : ' (zipped anyway)'}` : ''}.
            {' '}Each link is made when you click and works for {fmtInt(linkMinutes)} minutes; a download that has started finishes.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 1 }}>
            {parts.map((p) => {
              const key = `${exp.exportId}-${p.partNo}`;
              return (
                <Tooltip key={key} title={p.fileName || ''}>
                  <span>
                    <Button
                      size="small" variant="contained" sx={primaryButtonSx} disabled={!!partBusy}
                      startIcon={partBusy === key ? <CircularProgress size={14} color="inherit" /> : <MdDownload />}
                      onClick={() => onPart(exp, p.partNo)}
                    >
                      Part {p.partNo} of {p.partCount || exp.partCount || parts.length} · {fmtSize(p.bytes)} · {fmtInt(p.files)} files
                    </Button>
                  </span>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ===========================================================================
const DownloadDialog = ({ open, onClose, jobId, config, notify }) => {
  const features = (config && config.features) || {};
  const zipReady = features.zipExports === true;
  const linkMinutes = Number(config && config.exportLinkMinutes) || 15;
  const partGb = Math.round((Number(config && config.exportMaxPartBytes) || 2e9) / 1e8) / 10;
  const folderSupported = canPickFolder();
  const [selection, setSelection] = useState('approved');
  const [skipNames, setSkipNames] = useState(true);
  const [replaceSame, setReplaceSame] = useState(false); // Save into a folder: replace a different file with the same name
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [showNames, setShowNames] = useState(false);
  const [run, setRun] = useState(null);
  const [exportsList, setExportsList] = useState(null);
  const [exportsError, setExportsError] = useState('');
  const [buildBusy, setBuildBusy] = useState(false);
  const [partBusy, setPartBusy] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const stopRef = useRef(false);
  const controllersRef = useRef(new Set());
  const statsRef = useRef(null);
  const summarySeq = useRef(0);
  const summaryCache = useRef({});
  const running = !!run && run.status === 'running';

  // ---- shared choice: the summary (pageSize 1, so only one link is presigned) ------------------------
  const loadSummary = useCallback(async () => {
    if (!jobId) return;
    const key = `${selection}|${skipNames ? 1 : 0}`;
    if (summaryCache.current[key]) { setSummary(summaryCache.current[key]); setSummaryError(''); return; }
    const my = summarySeq.current + 1;
    summarySeq.current = my;
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const res = await rrsApi.downloadFiles(jobId, { selection, skipNamesOnImageServer: skipNames ? 'true' : 'false', page: 1, pageSize: 1 });
      const s = {
        total: Number(res.total) || 0,
        totalBytes: Number(res.totalBytes) || 0,
        leftOut: (res.leftOut && Number(res.leftOut.count)) || 0,
        names: (res.leftOut && Array.isArray(res.leftOut.names)) ? res.leftOut.names : [],
        checkedAt: res.imageServerCheckedAt || null,
        linkMinutes: res.linkMinutes || null,
      };
      summaryCache.current[key] = s;
      if (my === summarySeq.current) setSummary(s);
    } catch (e) {
      if (my === summarySeq.current) { setSummary(null); setSummaryError(serverText(e)); }
    } finally {
      if (my === summarySeq.current) setSummaryLoading(false);
    }
  }, [jobId, selection, skipNames]);

  const loadExports = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await rrsApi.listExports(jobId);
      setExportsList((res && res.items) || []);
      setExportsError('');
    } catch (e) {
      setExportsError(e && e.status === 404 ? 'Zip files are not available on the server yet.' : serverText(e));
      setExportsList([]);
    }
  }, [jobId]);

  useEffect(() => {
    if (!open) return;
    summaryCache.current = {};
    setRun(null);
    setShowNames(false);
    loadExports();
  }, [open, loadExports]);
  useEffect(() => { if (open) loadSummary(); }, [open, loadSummary]);

  const exportsActive = (exportsList || []).some((x) => x.status === 'queued' || x.status === 'building');
  usePolling(loadExports, 5000, open && exportsActive);

  // Progress is counted in a ref and copied to the screen twice a second.
  const now = useNow(running, 500);
  useEffect(() => {
    if (running && statsRef.current) setRun({ ...statsRef.current });
  }, [now, running]);

  const finishRun = (status, message) => {
    const s = statsRef.current;
    if (!s) return;
    s.status = status;
    s.message = message || '';
    s.current = '';
    s.endedAt = Date.now();
    setRun({ ...s });
  };

  const fetchPage = useCallback((page) => rrsApi.downloadFiles(jobId, {
    selection, skipNamesOnImageServer: skipNames ? 'true' : 'false', page, pageSize: PAGE_SIZE,
  }), [jobId, selection, skipNames]);

  const takeTotals = (res) => {
    const s = statsRef.current;
    if (!s || s.total !== null) return;
    s.total = Number(res.total) || 0;
    s.totalBytes = Number(res.totalBytes) || 0;
    s.leftOut = (res.leftOut && Number(res.leftOut.count)) || 0;
  };

  const fail = (name, why) => {
    const s = statsRef.current;
    s.failed += 1;
    if (s.failures.length < 50) s.failures.push({ name, why });
  };

  // ---- (a) save into a folder ---------------------------------------------------------------------------
  const saveOne = async (dir, item, ctx) => {
    const s = statsRef.current;
    const name = item.fileName;
    const want = item.bytes === null || item.bytes === undefined ? null : Number(item.bytes);
    let existed = false;
    try {
      const fh0 = await dir.getFileHandle(name);
      existed = true;
      const f0 = await fh0.getFile();
      if (want !== null && f0.size === want) {
        s.skipped += 1;
        s.bytesDone += want;
        return;
      }
      if (!ctx.replace) {
        // a different file with this name (a retouched copy, an older approved attempt): kept, never overwritten
        s.kept += 1;
        if (want !== null) s.bytesDone += want;
        if (s.keptNames.length < 50) s.keptNames.push(name);
        return;
      }
    } catch (e) {
      if (!e || e.name !== 'NotFoundError') { fail(name, (e && e.message) || 'could not read the folder'); return; }
    }
    if (stopRef.current) return;

    const ctrl = new AbortController();
    controllersRef.current.add(ctrl);
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, FILE_TIMEOUT_MS);
    let writable = null;
    let created = false;
    let counted = 0;
    s.current = name;
    const get = async (url) => {
      try {
        return await fetch(url, { signal: ctrl.signal, mode: 'cors', credentials: 'omit', cache: 'no-store' });
      } catch (fe) {
        if (fe && fe.name === 'TypeError' && !ctx.anyOk) throw new CorsProblem(CORS_TEXT);
        throw fe;
      }
    };
    try {
      let resp = await get(item.url);
      if (resp.status === 403) {
        const fresh = await ctx.reloadOnce();
        const again = fresh ? fresh.get(item.genId) : null;
        if (again && again.url) resp = await get(again.url);
      }
      if (!resp.ok || !resp.body) throw new Error(resp.status === 403 ? 'the link expired' : `HTTP ${resp.status}`);
      ctx.anyOk = true;
      const fh = await dir.getFileHandle(name, { create: true });
      created = !existed;
      writable = await fh.createWritable();
      const counter = new TransformStream({
        transform(chunk, controller) {
          counted += chunk.byteLength;
          s.bytesDone += chunk.byteLength;
          controller.enqueue(chunk);
        },
      });
      // preventClose: the temporary copy replaces the file only on close(), and close() only runs when every byte
      // arrived; a short download is aborted instead, so a file that was already there stays as it was
      await resp.body.pipeThrough(counter).pipeTo(writable, { signal: ctrl.signal, preventClose: true });
      if (want !== null && counted !== want) {
        const w = writable;
        writable = null;
        try { await w.abort(); } catch (ae) { /* already aborted */ }
        if (created) { try { await dir.removeEntry(name); } catch (re) { /* nothing to remove */ } }
        s.bytesDone -= counted;
        fail(name, `received ${fmtInt(counted)} of ${fmtInt(want)} bytes`);
        return;
      }
      await writable.close();
      writable = null; // the file is complete on disk
      const f = await fh.getFile();
      if (want !== null && f.size !== want) {
        // only a file this run created is removed; one that was already there is never deleted
        if (created) { try { await dir.removeEntry(name); } catch (re) { /* keep going */ } }
        s.bytesDone -= counted;
        fail(name, `size ${fmtInt(f.size)} instead of ${fmtInt(want)} bytes`);
        return;
      }
      s.saved += 1;
      if (existed) s.replaced += 1;
    } catch (e) {
      if (writable) { try { await writable.abort(); } catch (ae) { /* already aborted */ } }
      if (created) { try { await dir.removeEntry(name); } catch (re) { /* nothing to remove */ } }
      s.bytesDone -= counted;
      if (e instanceof CorsProblem) throw e;
      if (stopRef.current && !timedOut) return;
      fail(name, timedOut ? 'timed out after 120 s' : ((e && e.message) || 'failed'));
    } finally {
      clearTimeout(timer);
      controllersRef.current.delete(ctrl);
    }
  };

  const runFolder = async () => {
    let dir;
    try {
      dir = await window.showDirectoryPicker({ mode: 'readwrite', id: 'rug-room-shots' });
    } catch (e) {
      if (e && e.name === 'AbortError') return; // the folder picker was closed
      notify(errText(e) || 'The folder could not be opened.', 'error');
      return;
    }
    stopRef.current = false;
    statsRef.current = newStats('folder');
    setRun({ ...statsRef.current });
    const ctx = { anyOk: false, reloadOnce: null, replace: replaceSame };
    try {
      let page = 1;
      let pages = 1;
      while (page <= pages && !stopRef.current) {
        const thisPage = page;
        const res = await fetchPage(thisPage);
        takeTotals(res);
        pages = Math.max(1, Math.ceil((Number(res.total) || 0) / PAGE_SIZE));
        const queue = [...((res && res.items) || [])];
        let reload = null;
        // Links expired (403): this page of links is fetched again ONCE; every file of the page that got a 403
        // retries once with its new link, and the files still waiting take their new links too.
        ctx.reloadOnce = () => {
          if (!reload) {
            reload = fetchPage(thisPage).then((fresh) => {
              const map = new Map(((fresh && fresh.items) || []).map((x) => [x.genId, x]));
              queue.forEach((q, i) => { const n = map.get(q.genId); if (n) queue[i] = n; });
              return map;
            }).catch(() => null);
          }
          return reload;
        };
        const worker = async () => {
          while (queue.length && !stopRef.current) {
            const item = queue.shift();
            await saveOne(dir, item, ctx);
          }
        };
        await Promise.all(Array.from({ length: PARALLEL }, () => worker()));
        page += 1;
      }
      finishRun(stopRef.current ? 'stopped' : 'done', RERUN_TEXT);
    } catch (e) {
      stopRef.current = true;
      controllersRef.current.forEach((c) => c.abort());
      finishRun('error', e instanceof CorsProblem ? CORS_TEXT : serverText(e));
    }
  };

  // ---- other browsers: one by one ------------------------------------------------------------------------
  const runOneByOne = async () => {
    stopRef.current = false;
    statsRef.current = newStats('one');
    setRun({ ...statsRef.current });
    const s = statsRef.current;
    try {
      let page = 1;
      let pages = 1;
      while (page <= pages && !stopRef.current) {
        const res = await fetchPage(page);
        takeTotals(res);
        pages = Math.max(1, Math.ceil((Number(res.total) || 0) / PAGE_SIZE));
        const items = (res && res.items) || [];
        for (let i = 0; i < items.length && !stopRef.current; i += 1) {
          const it = items[i];
          s.current = it.fileName;
          const a = document.createElement('a');
          a.href = it.url;
          a.download = it.fileName; // cross-origin: the browser uses the link's Content-Disposition name
          a.rel = 'noopener';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a.remove();
          s.started += 1;
          s.bytesDone += Number(it.bytes) || 0;
          await sleep(ONE_BY_ONE_MS);
        }
        page += 1;
      }
      finishRun(stopRef.current ? 'stopped' : 'done', RERUN_TEXT);
    } catch (e) {
      finishRun('error', serverText(e));
    }
  };

  const stop = () => {
    stopRef.current = true;
    controllersRef.current.forEach((c) => c.abort());
  };

  // ---- (b) zip files ------------------------------------------------------------------------------------
  const buildZip = async () => {
    setBuildBusy(true);
    try {
      const res = await rrsApi.createExport(jobId, { selection, skipNamesOnImageServer: skipNames });
      notify(res && res.reused
        ? 'These zip files were already requested with the same images; they are listed below.'
        : 'Zip files requested. The worker builds them in the background; the list below updates every few seconds.', 'info');
      await loadExports();
    } catch (e) {
      notify(serverText(e), conflictCode(e) ? 'warning' : 'error');
    } finally {
      setBuildBusy(false);
    }
  };

  // The link is fetched on click and used at once: never rendered, kept or reused (build spec 9.7).
  const openPart = async (exp, partNo) => {
    setPartBusy(`${exp.exportId}-${partNo}`);
    try {
      const res = await rrsApi.getExport(exp.exportId);
      if (res && res.export) setExportsList((cur) => (cur || []).map((x) => (x.exportId === res.export.exportId ? res.export : x)));
      const part = ((res && res.parts) || []).find((p) => Number(p.partNo) === Number(partNo));
      if (!part || !part.url) {
        notify('This part is not available any more. Refresh the list.', 'warning');
        return;
      }
      window.location.assign(part.url);
    } catch (e) {
      notify(serverText(e), 'error');
    } finally {
      setPartBusy(null);
    }
  };

  const cancelExport = async (exp) => {
    setCancelBusy(true);
    try {
      const res = await rrsApi.cancelExport(exp.exportId);
      if (res && res.export) setExportsList((cur) => (cur || []).map((x) => (x.exportId === res.export.exportId ? res.export : x)));
      notify(`Zip request #${exp.exportId} ${exp.status === 'queued' ? 'cancelled' : 'is being cancelled'}.`, 'info');
      loadExports();
    } catch (e) {
      notify(serverText(e), conflictCode(e) ? 'warning' : 'error');
    } finally {
      setCancelBusy(false);
    }
  };

  const close = () => { if (!running) onClose(); };

  // ---- render ----------------------------------------------------------------------------------------------
  const pct = run && run.totalBytes ? Math.min(100, (run.bytesDone / run.totalBytes) * 100) : 0;
  const elapsed = run ? fmtElapsed((run.endedAt || Date.now()) - run.startedAt) : '';
  const nothing = !!summary && summary.total === 0;

  return (
    <Dialog open={open} onClose={running ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>
        Download images
        <Typography variant="body2" color="text.secondary">
          Job #{jobId}. Files are named like the single download: &lt;ItemID&gt;-ROOMAI-&lt;room&gt;.jpg.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Shared choices */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Images</Typography>
        <RadioGroup value={selection} onChange={(e) => setSelection(e.target.value)}>
          {SELECTIONS.map((o) => (
            <FormControlLabel
              key={o.value} value={o.value} disabled={running}
              control={<Radio size="small" />} label={<Typography variant="body2">{o.label}</Typography>}
            />
          ))}
        </RadioGroup>
        <FormControlLabel
          control={<Checkbox size="small" checked={skipNames} onChange={(e) => setSkipNames(e.target.checked)} disabled={running} />}
          label={<Typography variant="body2">Leave out images whose file name already exists on the image server</Typography>}
        />
        <Box sx={{ ml: 4, mb: 1 }}>
          {summaryLoading && <CircularProgress size={16} />}
          {summaryError && <Alert severity="error" sx={{ my: 0.5 }}>{summaryError}</Alert>}
          {summary && !summaryLoading && (
            <>
              <Typography variant="body2">
                {fmtInt(summary.total)} image{summary.total === 1 ? '' : 's'} · {fmtSize(summary.totalBytes)}
                {skipNames ? ` · ${fmtInt(summary.leftOut)} left out (name already on the image server)` : ''}
              </Typography>
              {skipNames && summary.leftOut > 0 && (
                <>
                  <Button
                    size="small" onClick={() => setShowNames((v) => !v)} endIcon={showNames ? <MdExpandLess /> : <MdExpandMore />}
                    sx={{ px: 0.5 }}
                  >
                    {showNames ? 'Hide' : 'Show'} the names{summary.leftOut > summary.names.length ? ` (first ${fmtInt(summary.names.length)})` : ''}
                  </Button>
                  <Collapse in={showNames} unmountOnExit>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 160, overflowY: 'auto' }}>
                      {summary.names.map((n) => (
                        <li key={n}><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{n}</Typography></li>
                      ))}
                    </Box>
                  </Collapse>
                </>
              )}
              {summary.checkedAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Names on the image server as of the last room scan, {fmtNy(summary.checkedAt)} (New York).
                </Typography>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* (a) Save into a folder */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <MdFolderOpen />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Save into a folder</Typography>
        </Stack>
        {folderSupported ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Pick a folder once; every image is saved into it, three at a time. Images already in the folder with the
              same size are skipped, so you can stop and run again to resume. A different file with the same name (for
              example a retouched copy) is kept and listed, unless you tick the box below.
            </Typography>
            <FormControlLabel
              sx={{ mb: 0.5 }}
              control={<Checkbox size="small" checked={replaceSame} onChange={(e) => setReplaceSame(e.target.checked)} disabled={running} />}
              label={<Typography variant="body2">Replace files with the same name (for example to take a newer approved attempt)</Typography>}
            />
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 1 }}>{FALLBACK_TEXT}</Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {folderSupported && (
            <Button
              variant="contained" sx={primaryButtonSx} startIcon={<MdFolderOpen />}
              disabled={running || nothing || summaryLoading} onClick={runFolder}
            >
              {run && run.mode === 'folder' && run.status !== 'running' ? 'Choose a folder and run again' : 'Choose a folder and save'}
            </Button>
          )}
          <Button
            variant={folderSupported ? 'outlined' : 'contained'} sx={folderSupported ? undefined : primaryButtonSx}
            startIcon={<MdDownload />} disabled={running || nothing || summaryLoading} onClick={runOneByOne}
          >
            Download one by one
          </Button>
          {running && (
            <Button color="error" variant="outlined" startIcon={<MdStop />} onClick={stop}>Stop</Button>
          )}
        </Stack>
        {run && (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5 }}>
            <LinearProgress
              variant={run.totalBytes ? 'determinate' : 'indeterminate'} value={pct}
              sx={{ height: 6, borderRadius: 3, mb: 0.75, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }}
            />
            <Typography variant="body2">
              {run.mode === 'folder'
                ? `${fmtInt(run.saved)} saved${run.replaced ? ` (${fmtInt(run.replaced)} replaced)` : ''} · ${fmtInt(run.skipped)} already in the folder${run.kept ? ` · ${fmtInt(run.kept)} kept (a different file with the same name is in the folder)` : ''} · ${fmtInt(run.failed)} failed · ${fmtInt(run.leftOut)} left out`
                : `${fmtInt(run.started)} started · ${fmtInt(run.leftOut)} left out`}
              {run.total !== null ? ` · of ${fmtInt(run.total)}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {fmtSize(run.bytesDone)} of {run.totalBytes !== null ? fmtSize(run.totalBytes) : '…'} · {elapsed}
              {run.status === 'running' && run.current ? ` · ${run.current}` : ''}
            </Typography>
            {run.status === 'done' && <Typography variant="body2" sx={{ mt: 0.5 }}>Finished. {run.message}</Typography>}
            {run.status === 'stopped' && <Typography variant="body2" sx={{ mt: 0.5 }}>Stopped. {run.message}</Typography>}
            {run.status === 'error' && <Alert severity="warning" sx={{ mt: 0.75 }}>{run.message}</Alert>}
            {run.keptNames && run.keptNames.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Kept as they are{run.kept > run.keptNames.length ? ` (first ${fmtInt(run.keptNames.length)})` : ''}; tick Replace files with the same name and run again to overwrite them:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 120, overflowY: 'auto' }}>
                  {run.keptNames.map((n) => (
                    <li key={n}><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{n}</Typography></li>
                  ))}
                </Box>
              </Box>
            )}
            {run.failures.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 120, overflowY: 'auto' }}>
                {run.failures.map((f) => (
                  <li key={f.name}>
                    <Typography variant="caption"><span style={{ fontFamily: 'monospace' }}>{f.name}</span>: {f.why}</Typography>
                  </li>
                ))}
              </Box>
            )}
          </Paper>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* (b) Zip files */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <MdArchive />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Zip files</Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={loadExports}>Refresh</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The worker builds zip files of up to {partGb} GB each (images at the top level plus a manifest CSV) with the choices above.
          It takes a few minutes; you can close this dialog meanwhile.
        </Typography>
        <Tooltip title={zipReady ? '' : NOT_READY_TEXT}>
          <span>
            <Button
              variant="outlined" startIcon={buildBusy ? <CircularProgress size={14} /> : <MdArchive />}
              disabled={!zipReady || buildBusy || nothing} onClick={buildZip}
            >
              Build zip files
            </Button>
          </span>
        </Tooltip>
        {!zipReady && features.zipExportsReason !== 'loading' && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{NOT_READY_TEXT}</Typography>
        )}
        {exportsError && <Alert severity="warning" sx={{ mt: 1 }}>{exportsError}</Alert>}
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {exportsList === null && <CircularProgress size={18} />}
          {(exportsList || []).map((exp) => (
            <ExportRow
              key={exp.exportId} exp={exp} onPart={openPart} onCancel={cancelExport} partBusy={partBusy} cancelBusy={cancelBusy}
              linkMinutes={linkMinutes}
            />
          ))}
          {exportsList && exportsList.length === 0 && !exportsError && (
            <Typography variant="caption" color="text.secondary">No zip files requested for this job yet.</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {running && <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>Stop the download to close this dialog.</Typography>}
        <Button onClick={close} disabled={running}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadDialog;
